// The Reportes tabs, declaratively: what each one asks, which KPIs answer it,
// which columns its table has, and how one API row becomes one table row.
//
// The view stays generic — it renders whatever these describe — so adding a tab
// or a column is a change here, not in five components.

import { ACCENT, INK, SOFT, FAINT, SOURCE_LABELS } from "@/utils/charts";

export const GOLD = "#c89b3c";
export const GREEN = "#2c6e49";
export const BLUE = "#2353b8";

// PropertyCategory's vocabulary (backend: living|commercial|land|parking|other)
export const CATEGORY_LABELS = {
  living: "Vivienda",
  commercial: "Comercial",
  land: "Terrenos",
  parking: "Cocheras",
  other: "Otros"
};
export const categoryLabel = c => CATEGORY_LABELS[c] || c || "—";

// funnel stage keys (audienceReport.funnel / onboardingFunnel)
const STAGE_LABELS = {
  searches: "Búsquedas",
  results: "Con resultados",
  visits: "Visitas",
  actions: "Acciones",
  inquiries: "Consultas",
  won: "Ganadas",
  shown: "Invitación mostrada",
  accepted: "Hicieron clic",
  signup: "Crearon cuenta"
};
const stageLabel = k => STAGE_LABELS[k] || k;

const TIERS = {
  hot: ["Caliente", "#fdecea", ACCENT],
  warm: ["Tibio", "#fdeecf", "#8a5a00"],
  cold: ["Frío", "#eef1f4", "#6a727b"]
};

const STATES = {
  pending: ["Pendiente", "#fdeecf", "#8a5a00"],
  ongoing: ["En curso", "#e1ecff", BLUE],
  on_hold: ["Esperando", "#efe3f7", "#6a3a8f"],
  watching: ["Observación", "#e9eef2", "#43596b"],
  visit: ["Visita", "#e1ecff", BLUE],
  negotiation: ["Negociación", "#fdeecf", "#8a5a00"],
  success: ["Ganada", "#e3f3e8", GREEN],
  lost: ["Perdida", "#ece9e3", "#6a645a"]
};

// ---------- small formatters ----------
const num = n => (n == null ? "—" : Number(n).toLocaleString("es-AR"));
const es = n => String(n).replace(".", ",");
export const pct = n => (n == null ? "—" : es(Math.round(Number(n) * 10) / 10) + "%");
const daysAgo = d => (d == null ? "—" : d === 0 ? "hoy" : "hace " + d + " d");

// days between a timestamp and now
export function daysSince(at) {
  if (!at) return null;
  const ms = Date.now() - new Date(at).getTime();
  return ms < 0 ? 0 : Math.floor(ms / 86400000);
}

// cell builders — mirror the design's L/N/B helpers
const L = (text, o) => ({ text, align: "start", weight: 400, color: INK, ...(o || {}) });
const N = (text, o) => ({ text: text == null ? "—" : String(text), align: "end", weight: 600, color: INK, ...(o || {}) });
const B = (badge, bg, fg) => ({ badge, badgeBg: bg, badgeFg: fg, align: "start" });

const tier = t => TIERS[t] || TIERS.cold;
const state = s => STATES[s] || ["Sin estado", "#ece9e3", "#6a645a"];

function propLabel(property, t) {
  if (!property || !property.propertyType) return "Propiedad";
  const key = "property.type." + String(property.propertyType).toLowerCase();
  const label = t(key);
  return label === key ? property.propertyType : label;
}

// intent.price → "USD 80–150k"
function budgetLabel(intent) {
  const price = intent && intent.price;
  if (!price) return null;
  const cur = price.USD ? "USD" : Object.keys(price)[0];
  const band = cur && price[cur];
  if (!band || !band.max) return null;
  const k = v => (v >= 1000 ? Math.round(v / 1000) + "k" : String(Math.round(v)));
  return `${cur} ${k(band.min || 0)}–${k(band.max)}`;
}

// ---------- tabs ----------
// `columns` mirror the design: key, label, width (CSS grid track), min width for
// the table's minimum, and right-alignment for numerics.
export const TABS = [
  {
    key: "demanda",
    label: "Demanda",
    title: "Demanda",
    question: "¿Hay más o menos interés que en el período anterior?",
    heroLabel: "Visitas por día",
    tableTitle: "Propiedades por rendimiento",
    defaultSort: "range_visits",
    serverSorted: true,
    columns: [
      { key: "type", label: "Propiedad", w: "minmax(170px,2.1fr)", min: 170 },
      { key: "zone", label: "Zona", w: "minmax(112px,1fr)", min: 112 },
      { key: "price", label: "Precio", w: "112px", min: 112 },
      { key: "range_visits", label: "Visitas", w: "84px", min: 84, right: true },
      { key: "inquiries", label: "Consultas", w: "92px", min: 92, right: true },
      { key: "conversion", label: "Conv.", w: "78px", min: 78, right: true },
      { key: "last", label: "Últ. visita", w: "110px", min: 110, right: true }
    ]
  },
  {
    key: "consultas",
    label: "Consultas",
    title: "Consultas",
    question: "¿Qué entró y qué está esperando respuesta?",
    heroLabel: "Consultas nuevas por día",
    tableTitle: "Bandeja de consultas",
    defaultSort: "waiting",
    serverSorted: true,
    columns: [
      { key: "state", label: "Estado", w: "122px", min: 122 },
      { key: "name", label: "Contacto", w: "minmax(150px,1.2fr)", min: 150 },
      { key: "prop", label: "Propiedad", w: "minmax(180px,1.6fr)", min: 180 },
      { key: "score", label: "Lead", w: "124px", min: 124 },
      { key: "created_at", label: "Entró", w: "92px", min: 92, right: true },
      { key: "waiting", label: "Esperando", w: "104px", min: 104, right: true }
    ]
  },
  {
    key: "leads",
    label: "Leads",
    title: "Leads",
    question: "¿A quién llamo hoy?",
    heroLabel: "Visitantes por día",
    tableTitle: "Leads por puntaje",
    defaultSort: "score",
    defaultHidden: { budget: true, searches: true },
    columns: [
      { key: "score", label: "Lead", w: "126px", min: 126 },
      { key: "name", label: "Contacto", w: "minmax(160px,1.5fr)", min: 160 },
      { key: "op", label: "Intención", w: "116px", min: 116 },
      { key: "zone", label: "Zona", w: "minmax(104px,1fr)", min: 104 },
      { key: "budget", label: "Presupuesto", w: "130px", min: 130 },
      { key: "searches", label: "Búsq.", w: "72px", min: 72, right: true },
      { key: "visits", label: "Visitas", w: "78px", min: 78, right: true },
      { key: "inq", label: "Consultas", w: "92px", min: 92, right: true },
      { key: "last", label: "Actividad", w: "100px", min: 100, right: true }
    ]
  },
  {
    key: "propietarios",
    label: "Propietarios",
    title: "Propietarios",
    question: "¿Qué le muestro al dueño esta semana?",
    heroLabel: "Visitas por día",
    tableTitle: "Cartera por propietario",
    defaultSort: "range_visits",
    serverSorted: true,
    columns: [
      { key: "type", label: "Propiedad", w: "minmax(180px,2fr)", min: 180 },
      { key: "owner", label: "Propietario", w: "minmax(130px,1.1fr)", min: 130 },
      { key: "published", label: "Estado", w: "130px", min: 130 },
      { key: "visits", label: "Visitas", w: "84px", min: 84, right: true },
      { key: "range_visits", label: "Período", w: "92px", min: 92, right: true },
      { key: "inquiries", label: "Consultas", w: "96px", min: 96, right: true },
      { key: "open_inquiries", label: "Abiertas", w: "92px", min: 92, right: true }
    ]
  },
  {
    key: "cuentas",
    label: "Cuentas",
    title: "Cuentas",
    question: "¿Crece el registro de usuarios?",
    heroLabel: "Usuarios nuevos por día",
    tableTitle: "Usuarios registrados",
    defaultSort: "reg",
    defaultHidden: { searches: true },
    columns: [
      { key: "name", label: "Usuario", w: "minmax(150px,1.4fr)", min: 150 },
      { key: "mail", label: "Email", w: "minmax(180px,1.6fr)", min: 180 },
      { key: "method", label: "Método", w: "108px", min: 108 },
      { key: "reg", label: "Registro", w: "108px", min: 108, right: true },
      { key: "favs", label: "Favs", w: "76px", min: 76, right: true },
      { key: "searches", label: "Búsq.", w: "80px", min: 80, right: true },
      { key: "inq", label: "Consultas", w: "96px", min: 96, right: true },
      { key: "last", label: "Actividad", w: "104px", min: 104, right: true }
    ]
  }
];

export const TAB_KEYS = TABS.map(t => t.key);

// ---------- one API row → one table row ----------
// Returns { cells: {colKey: cell}, mobile: {...}, sortKeys: {...} }. The sort
// keys are only used by the tabs that sort in the browser (leads, cuentas).
export function mapRow(tabKey, r, t) {
  if (tabKey === "demanda") {
    return {
      cells: {
        type: L(propLabel(r.property, t), { weight: 600, sub: r.property && r.property.fullAddress }),
        zone: L(r.zone || "—", { color: SOFT }),
        price: L(r.price || "—", { color: "#56504a", weight: 500 }),
        range_visits: N(num(r.rangeVisits), { weight: 700 }),
        inquiries: N(num(r.inquiries), { color: r.inquiries ? GREEN : FAINT, weight: 700 }),
        conversion: N(pct(r.conversion), { color: SOFT }),
        last: N(daysAgo(r.daysSinceLastVisit), { color: FAINT, weight: 400 })
      },
      mobile: {
        title: propLabel(r.property, t),
        sub: [r.property && r.property.fullAddress, r.price].filter(Boolean).join(" · "),
        stats: [
          { label: "visitas", value: num(r.rangeVisits) },
          { label: "consultas", value: num(r.inquiries), color: r.inquiries ? GREEN : FAINT },
          { label: "conv.", value: pct(r.conversion), color: SOFT }
        ]
      }
    };
  }

  if (tabKey === "consultas") {
    const st = state(r.state);
    const tr = tier(r.leadTier);
    const waiting = r.attended ? "atendida" : r.waitingDays != null ? r.waitingDays + " d" : "—";
    const waitColor = r.attended ? GREEN : r.waitingDays > 5 ? ACCENT : r.waitingDays != null ? "#8a5a00" : FAINT;
    return {
      cells: {
        state: B(st[0], st[1], st[2]),
        name: L(r.name, { weight: 600, sub: r.phone || r.email }),
        prop: L(r.property ? r.property.fullAddress : "Consulta general", { color: "#56504a", sub: r.message }),
        score: B(tr[0] + (r.leadScore != null ? " · " + r.leadScore : ""), tr[1], tr[2]),
        created_at: N(daysAgo(daysSince(r.createdAtTime || r.createdAt)), { color: FAINT, weight: 400 }),
        waiting: N(waiting, { color: waitColor, weight: !r.attended && r.waitingDays > 5 ? 700 : 600 })
      },
      mobile: {
        badge: st[0],
        badgeBg: st[1],
        badgeFg: st[2],
        title: r.name,
        sub: r.property ? r.property.fullAddress : "Consulta general",
        stats: [
          { label: "esperando", value: waiting, color: waitColor },
          { label: "puntaje", value: r.leadScore == null ? "—" : r.leadScore },
          { label: "entró", value: daysAgo(daysSince(r.createdAtTime || r.createdAt)), color: FAINT }
        ]
      }
    };
  }

  if (tabKey === "leads") {
    const tr = tier(r.leadTier);
    const budget = budgetLabel(r.intent);
    const last = daysSince(r.lastSeen);
    return {
      cells: {
        score: B(tr[0] + (r.leadScore != null ? " · " + r.leadScore : ""), tr[1], tr[2]),
        name: L(r.contactName || "Visitante anónimo", {
          weight: 600,
          sub: r.identified ? r.contactEmail : "sin identificar"
        }),
        op: L(r.topOperation === "sell" ? "Venta" : r.topOperation === "rent" ? "Alquiler" : "—", {
          color: "#56504a",
          weight: 500,
          sub: r.topCategory ? categoryLabel(r.topCategory) : null
        }),
        zone: L(r.topZone || "—", { color: SOFT }),
        budget: L(budget || "—", { color: SOFT }),
        searches: N(num(r.searchesCount)),
        visits: N(num(r.visitsCount)),
        inq: N(num(r.inquiriesCount), { color: r.inquiriesCount ? GREEN : FAINT, weight: 700 }),
        last: N(daysAgo(last), { color: FAINT, weight: 400 })
      },
      sortKeys: {
        score: r.leadScore || 0,
        name: r.contactName || "",
        op: r.topOperation || "",
        zone: r.topZone || "",
        budget: budget || "",
        searches: r.searchesCount || 0,
        visits: r.visitsCount || 0,
        inq: r.inquiriesCount || 0,
        last: -(last == null ? 99999 : last)
      },
      mobile: {
        badge: tr[0] + (r.leadScore != null ? " · " + r.leadScore : ""),
        badgeBg: tr[1],
        badgeFg: tr[2],
        title: r.contactName || "Visitante anónimo",
        sub: [r.topOperation === "sell" ? "Venta" : r.topOperation === "rent" ? "Alquiler" : null, r.topZone, budget]
          .filter(Boolean)
          .join(" · "),
        stats: [
          { label: "búsq.", value: num(r.searchesCount) },
          { label: "visitas", value: num(r.visitsCount) },
          { label: "consultas", value: num(r.inquiriesCount), color: r.inquiriesCount ? GREEN : FAINT }
        ]
      }
    };
  }

  if (tabKey === "propietarios") {
    return {
      cells: {
        type: L(propLabel(r.property, t), { weight: 600, sub: r.property && r.property.fullAddress }),
        owner: L(r.ownerName || "—", { color: "#56504a", weight: 500 }),
        published: B(
          r.published ? "Publicada" : "No publicada",
          r.published ? "#e3f3e8" : "#ece9e3",
          r.published ? GREEN : "#6a645a"
        ),
        visits: N(num(r.visits), { weight: 700 }),
        range_visits: N(num(r.rangeVisits)),
        inquiries: N(num(r.inquiries), { color: r.inquiries ? GREEN : FAINT, weight: 700 }),
        open_inquiries: N(r.openInquiries || "—", { color: r.openInquiries ? "#8a5a00" : FAINT })
      },
      mobile: {
        badge: r.published ? "Publicada" : "No publicada",
        badgeBg: r.published ? "#e3f3e8" : "#ece9e3",
        badgeFg: r.published ? GREEN : "#6a645a",
        title: propLabel(r.property, t),
        sub: [r.property && r.property.fullAddress, r.ownerName].filter(Boolean).join(" · "),
        stats: [
          { label: "visitas", value: num(r.visits) },
          { label: "período", value: num(r.rangeVisits) },
          { label: "consultas", value: num(r.inquiries), color: r.inquiries ? GREEN : FAINT }
        ]
      }
    };
  }

  // cuentas
  const google = r.signupMethod === "google";
  const reg = daysSince(r.registeredAt);
  const last = daysSince(r.lastActiveAt);
  return {
    cells: {
      name: L(r.name || "Sin nombre", { weight: 600 }),
      mail: L(r.email, { color: SOFT }),
      method: B(google ? "Google" : "Email", google ? "#e1ecff" : "#fdecea", google ? BLUE : ACCENT),
      reg: N(daysAgo(reg), { color: SOFT, weight: 400 }),
      favs: N(num(r.favoritesCount)),
      searches: N(num(r.savedSearchesCount)),
      inq: N(num(r.inquiriesCount), { color: r.inquiriesCount ? GREEN : FAINT, weight: 700 }),
      last: N(daysAgo(last), { color: FAINT, weight: 400 })
    },
    sortKeys: {
      name: r.name || "",
      mail: r.email || "",
      method: r.signupMethod || "",
      reg: -(reg == null ? 99999 : reg),
      favs: r.favoritesCount || 0,
      searches: r.savedSearchesCount || 0,
      inq: r.inquiriesCount || 0,
      last: -(last == null ? 99999 : last)
    },
    mobile: {
      badge: google ? "Google" : "Email",
      badgeBg: google ? "#e1ecff" : "#fdecea",
      badgeFg: google ? BLUE : ACCENT,
      title: r.name || "Sin nombre",
      sub: r.email,
      stats: [
        { label: "favs", value: num(r.favoritesCount) },
        { label: "búsq.", value: num(r.savedSearchesCount) },
        { label: "consultas", value: num(r.inquiriesCount), color: r.inquiriesCount ? GREEN : FAINT }
      ]
    }
  };
}

// ---------- detail panels (level 3) ----------
const bars = (rows, color) => {
  const max = Math.max(1, ...rows.map(r => r[1]));
  return rows.map(([label, value]) => ({
    label,
    value: num(value),
    color: color || ACCENT,
    width: Math.round((value / max) * 100) + "%"
  }));
};

const funnelRows = rows => {
  const first = rows[0] ? rows[0][1] || 1 : 1;
  return rows.map(([label, value], i) => ({
    label,
    value: num(value),
    width: Math.round((value / first) * 100) + "%",
    note: i > 0 ? Math.round((value / (rows[i - 1][1] || 1)) * 100) + "%" : ""
  }));
};

const buckets = list => (list || []).map(b => [b.label, b.count]);

// Build the panels for a tab from the loaded reports. Anything without data is
// dropped, so a quiet period collapses instead of drawing empty bars.
export function buildPanels(tabKey, { visit, audience, area, budget, leads } = {}) {
  const out = [];
  const push = p => {
    if (p && p.rows && p.rows.length) out.push(p);
  };

  if (tabKey === "demanda") {
    push({
      title: "Fuentes de tráfico",
      hint: "de dónde vienen",
      kind: "bars",
      rows: bars(((visit && visit.sources) || []).map(s => [SOURCE_LABELS[s.label] || s.label, s.count]))
    });
    const ds = (audience && audience.demandVsSupply) || [];
    if (ds.length) {
      const max = Math.max(1, ...ds.map(d => d.demand));
      out.push({
        title: "Demanda vs oferta",
        hint: "dónde falta stock",
        kind: "dual",
        rows: ds.map(d => ({
          label: categoryLabel(d.category),
          note: `${d.demand} vs ${d.supply}`,
          width: Math.round((d.demand / max) * 100) + "%",
          width2: Math.round((d.supply / max) * 100) + "%"
        }))
      });
    }
    // The zone map and the budget breakdown keep their own controls and
    // drill-downs — they're folded in whole, full width, rather than flattened
    // into bars that would lose the map and the segment drawer.
    out.push({ title: "Zonas con más demanda", hint: "mapa y ranking", kind: "slot", slot: "areas", full: true, rows: [1] });
    out.push({ title: "Demanda por presupuesto", hint: "con drill-down", kind: "slot", slot: "budget", full: true, rows: [1] });
    return out;
  }

  if (tabKey === "consultas") {
    push({
      title: "Más consultadas",
      hint: "top 5",
      kind: "bars",
      rows: bars(
        ((visit && visit.topInquired) || [])
          .slice(0, 5)
          .filter(x => x.property)
          .map(x => [x.property.fullAddress, x.count]),
        GREEN
      )
    });
    push({
      title: "Consultas por semana",
      hint: "",
      kind: "bars",
      rows: bars(buckets(audience && audience.weeklyInquiries), INK)
    });
    const median = visit && visit.medianFirstResponseDays;
    const stale = visit && visit.unansweredOver5d;
    const conv = audience && audience.conversion;
    out.push({
      title: "Demora de respuesta",
      hint: "",
      kind: "stats",
      rows: [
        { value: median == null ? "—" : es(median) + " d", label: "mediana hasta la primera respuesta" },
        { value: stale == null ? "—" : num(stale), label: "consultas abiertas con más de 5 días sin respuesta" },
        { value: conv == null ? "—" : pct(conv), label: "de las consultas terminan ganadas" }
      ]
    });
    return out;
  }

  if (tabKey === "leads") {
    push({
      title: "Embudo de conversión",
      hint: "búsqueda → cierre",
      kind: "funnel",
      rows: funnelRows(buckets(audience && audience.funnel).map(([l, c]) => [stageLabel(l), c]))
    });
    const counts = { hot: 0, warm: 0, cold: 0 };
    (leads || []).forEach(l => {
      if (counts[l.leadTier] != null) counts[l.leadTier] += 1;
    });
    push({
      title: "Por temperatura",
      hint: (leads || []).length + " con intención",
      kind: "bars",
      rows: bars([["Caliente", counts.hot], ["Tibio", counts.warm], ["Frío", counts.cold]])
    });
    push({
      title: "Zonas de interés",
      hint: "top 5",
      kind: "bars",
      rows: bars(buckets(audience && audience.topZones).slice(0, 5), BLUE)
    });
    push({
      title: "Intención declarada",
      hint: "",
      kind: "bars",
      rows: bars(
        buckets(audience && audience.operationSplit).map(([l, c]) => [
          l === "sell" ? "Venta" : l === "rent" ? "Alquiler" : l,
          c
        ]),
        GOLD
      )
    });
    return out;
  }

  if (tabKey === "propietarios") {
    const t2f = audience && audience.timeToFirstInquiryDays;
    out.push({
      title: "Tiempos",
      hint: "",
      kind: "stats",
      rows: [
        { value: t2f == null ? "—" : es(t2f) + " d", label: "mediana hasta la primera consulta" },
        {
          value: audience && audience.newListingsLiquidity != null ? pct(audience.newListingsLiquidity) : "—",
          label: "de los listados nuevos consultados ≤14 días"
        },
        {
          value: audience && audience.wonInquiries != null ? num(audience.wonInquiries) : "—",
          label: "consultas ganadas en el período"
        }
      ]
    });
    return out;
  }

  // cuentas
  push({
    title: "Embudo de la invitación",
    hint: "",
    kind: "funnel",
    rows: funnelRows(buckets(audience && audience.onboardingFunnel).map(([l, c]) => [stageLabel(l), c]))
  });
  push({
    title: "Cuentas por método",
    hint: "",
    kind: "bars",
    rows: bars(
      buckets(audience && audience.signupsByMethod).map(([l, c]) => [
        l === "google" ? "Google" : l === "email" ? "Email" : l,
        c
      ]),
      BLUE
    )
  });
  return out;
}
